import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ErrorMessages } from '@constants/constants';
import { paginationParams } from '@shared/config/table.config';
import {
  Contractor,
  ContractorsCountByMgr,
  ManagerData,
  TabLabels,
} from '@interfaces/vendor-management-model';
import {
  columnDefs,
  StaticTexts,
  tabsData,
} from './contractors-managers-view.config';
import { SearchService } from '@services/search.service';
import { IEmeraldTabData } from '@emerald/nxcore';
import { DataService } from '@services/hiring-manger-profiledata.service';
import { AuthService } from '@core/auth.service';
import { EmployeeData } from '@interfaces/hiring-manager.model';
import { GlobalErrorService } from '@services/global-error.service';
import { ApiStatus, DefaultValues, LocationEnum } from '@constants/global.enum';
import { month } from '../contractor-head-count/contractor-head-count.config';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-contractors-managers-view',
  templateUrl: './contractors-managers-view.component.html',
  styleUrl: './contractors-managers-view.component.scss',
})
export class ContractorsManagersViewComponent {
  displayData: ContractorsCountByMgr[] = [];
  paginationParams = paginationParams;
  columnDefs = columnDefs;
  rowData: ContractorsCountByMgr[] = [];
  tabsData = tabsData;
  selectedTab!: IEmeraldTabData;
  tabLabel = TabLabels;
  employeeId: string | null = null;
  filterValue: string = '';
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = ErrorMessages.NoDataAvailable;
  isDataLoaded: boolean = true;
  selectedMonth: number = new Date().getMonth() + 1; // Default to current month
  selectedYear: number = new Date().getFullYear();   // Default to current year


  @Input() filterKeyword: string = '';
  @Output() totalCount: EventEmitter<number> = new EventEmitter<number>();

  constructor(
    private searchService: SearchService,
    private dataService: DataService,
    private authService: AuthService,
    private globalError: GlobalErrorService,
    private route: ActivatedRoute,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterKeyword'] && !changes['filterKeyword'].firstChange) {
      const searchValue = changes['filterKeyword'].currentValue?.toLowerCase() || '';
      this.filterValue = searchValue;

      if (searchValue) {
        this.displayData = this.rowData.filter((contractor) =>
          contractor.managerName?.toLowerCase().includes(searchValue)
        );
      } else {
        this.displayData = [...this.rowData];
      }

      this.totalCount.emit(this.displayData.length);
    }
  }

  ngOnInit(): void {
  this.route.queryParams.subscribe((params) => {
    const month = params['month'];
    const year = params['year'];

    if (month && year) {
      this.selectedMonth = Number(month);
      this.selectedYear = Number(year);
    } else {
      this.selectedMonth = new Date().getMonth() + 1; // Default to current month
      this.selectedYear = new Date().getFullYear();   // Default to current year
    }

    this.authService
      .getEmployeeDetails()
      .then((employeeData: EmployeeData | null | undefined) => {
        this.employeeId = employeeData?.employeeId || null;

        if (this.employeeId) {
          this.loadManagersData(this.employeeId, this.selectedMonth, this.selectedYear);
        } else {
          this.showError(StaticTexts.Alerts.EmployeeIdNotFound);
        }
      })
      .catch((error) => {
        this.globalError.logComponentError(error);
      });
  });
}
  private showError(message: string) {
    alert(message);
  }

  loadManagersData(employeeId: string, month: number, year: number): void {
    this.isDataLoaded = false;
    this.dataService.getContractors<Contractor>(employeeId, month, year).subscribe({
      next: (response) => {
        if (response.status === ApiStatus.SUCCESS) {
          const rawData = response.data || [];
          // Map rawData to match the Contractor type
          const contractors: Contractor[] = rawData.map((item) => ({
            reportingManager: item.reportingManager || DefaultValues.Unknown,
            city: item.city || LocationEnum.Remote,
            jobTitle: item.jobTitle || DefaultValues.NotAvailable,
          }));
          this.rowData = this.getManagersData(contractors);
          this.displayData = [...this.rowData]; // Initialize displayData with rowData
          this.totalCount.emit(this.displayData.length);
        } else {
          this.showError(StaticTexts.ErrorMessages.FailedToFetchManagerData);
        }
        this.isDataLoaded = true; // Reset loading state
      },
      error: (error) => {
        this.globalError.logComponentError(error);
        this.isDataLoaded = true; // Reset loading state
      },
    });
  }

  getManagersData(data: Contractor[]): ManagerData[] {
    const managerMap: { [key: string]: ManagerData } = {};

    data?.forEach((contractor) => {
      const managerName = contractor.reportingManager || DefaultValues.Unknown;
      const location = contractor.city || LocationEnum.Remote;
      const jobTitle = contractor.jobTitle || DefaultValues.NotAvailable;
      if (!managerMap[managerName]) {
        managerMap[managerName] = {
          managerName,
          designation: jobTitle,
          totalContractors: 0,
          Hyderabad: 0,
          NCR: 0,
          Remote: 0,
        };
      }

      managerMap[managerName].totalContractors++;
      if (location === LocationEnum.Hyderabad) {
        managerMap[managerName].Hyderabad++;
      } else if (location === LocationEnum.Noida || location === LocationEnum.Gurgaon) {
        managerMap[managerName].NCR++;
      } else {
        managerMap[managerName].Remote++;
      }
    });

    return Object.values(managerMap);
  }

  searchContractors(event: KeyboardEvent): void {
    const searchValue = this.searchService.getFilteredValue(event).toLowerCase(); // Get the search value
    this.filterValue = searchValue;
    this.displayData = this.displayData?.length
      ? this.displayData.filter((contractor) =>
          contractor.managerName?.toLowerCase().includes(searchValue)
        )
      : [];
    this.totalCount.emit(this.displayData.length);
  }

  clearInput(): void {
    this.filterValue = ''; // Clear the search input
    this.displayData = [...this.rowData]; // Reset the display data to the original data
    this.totalCount.emit(this.displayData.length); // Emit the total count
  }

  navigateToManagerProfile(managerId: number) {}
}
